import { MEDIA_ADAPTER_EVENT_TYPE as MAET } from "./mediaAdapter.js";
/**
 * BrowserMediaAdapter
 * -------------------
 *
 * Real browser implementation of MediaAdapter.
 *
 * Responsibilities:
 * - enumerate devices
 * - acquire/release local media
 * - enable/disable local tracks
 * - switch active input devices by reacquiring media
 * - optionally detect speaking from microphone input
 *
 * Non-responsibilities:
 * - reducer/domain ownership
 * - signaling
 * - RTCPeerConnection orchestration
 */
export class BrowserMediaAdapter {
    constructor(deps = {}) {
        this.listeners = new Set();
        this.localStream = null;
        this.lastAcquireInput = null;
        this.selectedAudioOutputDeviceId = null;
        this.speakingAudioContext = null;
        this.speakingSourceNode = null;
        this.speakingAnalyserNode = null;
        this.speakingIntervalId = null;
        this.lastIsSpeaking = false;
        this.handleDeviceChangeBound = () => {
            void this.handleDeviceChange();
        };
        this.mediaDevices = deps.mediaDevices ?? navigator.mediaDevices;
        this.createAudioContext =
            deps.createAudioContext ?? (() => new AudioContext());
        this.mediaDevices.addEventListener?.("devicechange", this.handleDeviceChangeBound);
    }
    async enumerateDevices() {
        const devices = await this.mediaDevices.enumerateDevices();
        const audioInputs = [];
        const videoInputs = [];
        const audioOutputs = [];
        for (const device of devices) {
            const mapped = {
                deviceId: device.deviceId,
                kind: device.kind,
                label: device.label,
                groupId: device.groupId,
            };
            switch (device.kind) {
                case "audioinput":
                    audioInputs.push(mapped);
                    break;
                case "videoinput":
                    videoInputs.push(mapped);
                    break;
                case "audiooutput":
                    audioOutputs.push(mapped);
                    break;
                default:
                    break;
            }
        }
        return {
            audioInputs,
            videoInputs,
            audioOutputs,
        };
    }
    async acquireLocalMedia(input) {
        try {
            const stream = await this.mediaDevices.getUserMedia(this.makeUserMediaConstraints(input));
            this.stopCurrentLocalStream();
            this.localStream = stream;
            this.lastAcquireInput = {
                audio: input.audio,
                video: input.video,
                ...(input.audioInputDeviceId !== undefined
                    ? { audioInputDeviceId: input.audioInputDeviceId }
                    : {}),
                ...(input.videoInputDeviceId !== undefined
                    ? { videoInputDeviceId: input.videoInputDeviceId }
                    : {}),
            };
            this.restartSpeakingDetectionIfPossible();
            await this.emitDevicesChanged();
            return {
                ok: true,
                hasAudioTrack: stream.getAudioTracks().length > 0,
                hasVideoTrack: stream.getVideoTracks().length > 0,
            };
        }
        catch (err) {
            return {
                ok: false,
                error: this.mapMediaError(err),
            };
        }
    }
    async stopLocalMedia() {
        this.stopSpeakingDetection();
        this.stopCurrentLocalStream();
        this.lastAcquireInput = null;
        this.lastIsSpeaking = false;
        this.emit({
            type: MAET.SPEAKING_CHANGED,
            isSpeaking: false,
        });
    }
    async setLocalAudioEnabled(enabled) {
        const stream = this.localStream;
        if (!stream)
            return;
        for (const track of stream.getAudioTracks()) {
            track.enabled = enabled;
        }
    }
    async setLocalVideoEnabled(enabled) {
        const stream = this.localStream;
        if (!stream)
            return;
        for (const track of stream.getVideoTracks()) {
            track.enabled = enabled;
        }
    }
    async selectAudioInputDevice(deviceId) {
        const previous = this.lastAcquireInput;
        if (!previous) {
            return {
                ok: false,
                error: {
                    code: "NO_PREVIOUS_MEDIA_REQUEST",
                    message: "Cannot switch audio input before local media has been acquired.",
                },
            };
        }
        return await this.acquireLocalMedia({
            ...previous,
            audio: true,
            ...(deviceId !== null ? { audioInputDeviceId: deviceId } : {}),
        });
    }
    async selectVideoInputDevice(deviceId) {
        const previous = this.lastAcquireInput;
        if (!previous) {
            return {
                ok: false,
                error: {
                    code: "NO_PREVIOUS_MEDIA_REQUEST",
                    message: "Cannot switch video input before local media has been acquired.",
                },
            };
        }
        return await this.acquireLocalMedia({
            ...previous,
            video: true,
            ...(deviceId !== null ? { videoInputDeviceId: deviceId } : {}),
        });
    }
    async selectAudioOutputDevice(deviceId) {
        this.selectedAudioOutputDeviceId = deviceId;
    }
    hasLocalMedia() {
        return this.localStream !== null;
    }
    subscribe(listener) {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    }
    /**
     * getLocalStream
     * --------------
     *
     * Runtime helper for concrete peer-connection adapters.
     *
     * This is intentionally not part of the MediaAdapter interface because the
     * controller/domain should not depend on raw MediaStream objects.
     */
    getLocalStream() {
        return this.localStream;
    }
    /**
     * getSelectedAudioOutputDeviceId
     * ------------------------------
     *
     * Optional runtime helper for demo/UI integrations.
     */
    getSelectedAudioOutputDeviceId() {
        return this.selectedAudioOutputDeviceId;
    }
    async handleDeviceChange() {
        await this.emitDevicesChanged();
    }
    async emitDevicesChanged() {
        const devices = await this.enumerateDevices();
        this.emit({
            type: MAET.DEVICES_CHANGED,
            devices,
        });
    }
    emit(event) {
        for (const listener of this.listeners) {
            listener(event);
        }
    }
    makeUserMediaConstraints(input) {
        const audio = input.audio === false
            ? false
            : input.audioInputDeviceId !== undefined
                ? {
                    deviceId: { exact: input.audioInputDeviceId },
                }
                : true;
        const video = input.video === false
            ? false
            : input.videoInputDeviceId !== undefined
                ? {
                    deviceId: { exact: input.videoInputDeviceId },
                }
                : true;
        return { audio, video };
    }
    stopCurrentLocalStream() {
        const stream = this.localStream;
        this.localStream = null;
        if (!stream)
            return;
        for (const track of stream.getTracks()) {
            track.stop();
        }
    }
    restartSpeakingDetectionIfPossible() {
        this.stopSpeakingDetection();
        const stream = this.localStream;
        if (!stream)
            return;
        const audioTracks = stream.getAudioTracks();
        if (audioTracks.length === 0) {
            this.lastIsSpeaking = false;
            this.emit({
                type: MAET.SPEAKING_CHANGED,
                isSpeaking: false,
            });
            return;
        }
        try {
            const audioContext = this.createAudioContext();
            const sourceNode = audioContext.createMediaStreamSource(stream);
            const analyserNode = audioContext.createAnalyser();
            analyserNode.fftSize = 2048;
            analyserNode.smoothingTimeConstant = 0.8;
            sourceNode.connect(analyserNode);
            const bufferLength = analyserNode.fftSize;
            const dataArray = new Uint8Array(bufferLength);
            this.speakingAudioContext = audioContext;
            this.speakingSourceNode = sourceNode;
            this.speakingAnalyserNode = analyserNode;
            this.lastIsSpeaking = false;
            this.speakingIntervalId = window.setInterval(() => {
                const analyser = this.speakingAnalyserNode;
                if (!analyser)
                    return;
                analyser.getByteTimeDomainData(dataArray);
                let sumSquares = 0;
                for (let i = 0; i < dataArray.length; i += 1) {
                    const normalized = (dataArray[i] ?? 128) / 128 - 1;
                    sumSquares += normalized * normalized;
                }
                const rms = Math.sqrt(sumSquares / dataArray.length);
                const isSpeaking = rms > 0.04;
                if (isSpeaking !== this.lastIsSpeaking) {
                    this.lastIsSpeaking = isSpeaking;
                    this.emit({
                        type: MAET.SPEAKING_CHANGED,
                        isSpeaking,
                    });
                }
            }, 200);
        }
        catch {
            this.lastIsSpeaking = false;
            this.emit({
                type: MAET.SPEAKING_CHANGED,
                isSpeaking: false,
            });
        }
    }
    stopSpeakingDetection() {
        if (this.speakingIntervalId !== null) {
            window.clearInterval(this.speakingIntervalId);
            this.speakingIntervalId = null;
        }
        try {
            this.speakingSourceNode?.disconnect();
        }
        catch {
            // best effort
        }
        try {
            this.speakingAnalyserNode?.disconnect();
        }
        catch {
            // best effort
        }
        this.speakingSourceNode = null;
        this.speakingAnalyserNode = null;
        const audioContext = this.speakingAudioContext;
        this.speakingAudioContext = null;
        if (audioContext) {
            void audioContext.close().catch(() => {
                // best effort
            });
        }
    }
    mapMediaError(err) {
        if (typeof err === "object" &&
            err !== null &&
            "name" in err &&
            typeof err.name === "string") {
            const message = "message" in err && typeof err.message === "string"
                ? err.message
                : "Unknown media error";
            return {
                code: err.name,
                message,
            };
        }
        return {
            code: "MEDIA_ERROR",
            message: err instanceof Error ? err.message : String(err),
        };
    }
}
